import { FunctionComponent, memo, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { A11y, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ConfigContext } from '../../context';
import { StoryCardContentImageVideo, StoryCardStatus, StoryCardType } from '../../services';
import {
  CardActions,
  FeedCardProps,
  GalleryItem,
  getCssVar,
  getStoryCardName,
  getStoryCardPreviewUrl,
  IconLabel,
  isCardMediaCompleted,
  Modal,
  useCardDate,
  useSwiperA11y,
  VideoPlayer,
} from '../../shared';

import classNames from 'classnames';
import classes from './FeedCardProfile.module.scss';

export const FeedCardProfile: FunctionComponent<FeedCardProps> = memo(({ card }) => {
  const { t } = useTranslation();

  const { showPostedTime = true } = useContext(ConfigContext).config.elements.card;

  const swiperA11y = useSwiperA11y();

  const { id: cardId, type, content, dateToDisplay, status } = card;

  const date = useCardDate(dateToDisplay);

  const { gallery } = content as StoryCardContentImageVideo;

  const galleryItem = gallery ? (gallery[0] as GalleryItem) : undefined;

  const previewUrl = getStoryCardPreviewUrl(card);

  const isImageGallery = useMemo(
    () => [StoryCardType.IMAGE].includes(type) && gallery && gallery.length > 1,
    [gallery, type]
  );

  const shouldRenderOverlay = useMemo(
    () => isImageGallery || (previewUrl && type === StoryCardType.VIDEO),
    [isImageGallery, previewUrl, type]
  );

  const iconId = useMemo(() => {
    if (isImageGallery) {
      return 'gallery';
    }

    return type.toLowerCase();
  }, [isImageGallery, type]);

  const iconColor = useMemo(
    () => (!previewUrl ? getCssVar('--card-overlay-icon-color') : '#ffffff'),
    [previewUrl]
  );

  const [openCardDetailsModal, setOpenCardDetailsModal] = useState(false);

  const cardDetailsModalBody = useMemo(() => {
    switch (type) {
      case StoryCardType.VIDEO:
        return (
          <VideoPlayer
            autoplay
            url={galleryItem?.video?.url}
            thumb={previewUrl}
            className={'card__modal-video'}
          />
        );
      case StoryCardType.IMAGE:
        return (
          <Swiper modules={[A11y, Navigation, Pagination]} {...swiperA11y} navigation pagination>
            {gallery.map(({ image }, index) => {
              if (!image) {
                return null;
              }

              const { url } = image;

              return (
                <SwiperSlide
                  key={`${url}-${index}`}
                  className={classes['card__modal-swiper-slide']}
                >
                  <img className={classes['card__modal-preview']} src={url} alt={''} />
                </SwiperSlide>
              );
            })}
          </Swiper>
        );
      default:
        return <></>;
    }
  }, [gallery, galleryItem?.video?.url, previewUrl, swiperA11y, type]);

  const cardDetailsClickHandler = useCallback(() => {
    if (!isCardMediaCompleted(card)) {
      toast.info(t('profileFeed.file-not-ready'));
      return;
    }

    setOpenCardDetailsModal(true);
  }, [card, t]);

  if (![StoryCardType.IMAGE, StoryCardType.VIDEO].includes(type)) {
    return null;
  }

  const cardName = getStoryCardName({ card, t });

  return (
    <>
      <button
        type={'button'}
        className={classNames(`card-${cardId}`, classes['card'], {
          [classes['card--not-published']]: status !== StoryCardStatus.PUBLISHED,
        })}
        aria-label={[cardName, showPostedTime && date].filter(Boolean).join(', ')}
        onClick={cardDetailsClickHandler}
      >
        <div
          className={classNames(classes['card__teaser'], {
            [classes['card__teaser--overlay']]: shouldRenderOverlay,
          })}
          style={{ backgroundImage: `url(${previewUrl})` }}
        >
          {shouldRenderOverlay && (
            <IconLabel
              className={classes['card__teaser-icon']}
              iconId={iconId}
              iconSize={32}
              color={iconColor}
              singleColor
            />
          )}
        </div>

        {showPostedTime && date && <div className={classes['card__posted-time']}>{date}</div>}
      </button>

      <CardActions card={card} isHorizontalFeedCard isProfileFeedCard />

      <Modal
        isOpen={openCardDetailsModal}
        body={cardDetailsModalBody}
        onClose={() => setOpenCardDetailsModal(false)}
        ariaLabel={cardName}
        previewMode
      />
    </>
  );
});
